import { css } from '@emotion/react'
import {
  SDirectiveStepsNav,
  SDirectiveStepsProcessIcon,
  SDirectiveStepsStack,
  SDirectiveStepsStep,
  SDirectiveStepsTextContainer,
  SDirectiveStepsValidationErrorIcon,
} from './DirectiveStepsNav.styled'
import { DirectiveSubsectionSpecification } from 'components/directive/utils/types'
import { Icon } from 'components/icon'
import { Portal } from 'components/portal/Portal'
import { BaseToast } from 'components/Toast'
import { Text } from 'components/Typography'
import ProcessFocus from 'images/extra-svg/ProcessFocus.svg'
import ProcessUnchecked from 'images/extra-svg/ProcessUnchecked.svg'
import { useRouter } from 'next/router'
import path from 'path'
import { ReactNode, useState } from 'react'
import { colors } from 'utils/colors'
import { useOuterClick, useWindowSize } from 'utils/hooks'
import { ApiType } from 'utils/types'

export type TDirectiveStepSubsection = DirectiveSubsectionSpecification<string> & {
  sectionKey: string
  isDone: boolean
  isTouched: boolean
  errors: ApiType['UserDirectiveConstraintResponse'][]
}
export type TDirectiveStep = {
  name: string
  mobileName: string
  value: string
  url: string
  isDone: boolean
  isCurrent: boolean
  hasError: string | null
  disabled: boolean
  subsections?: TDirectiveStepSubsection[]
}

export function DirectiveStepsNav(props: {
  dataTestIdPrefix: string
  steps: TDirectiveStep[]
  disabled?: boolean
  progress: ReactNode
  hideSteps?: boolean
}) {
  const router = useRouter()
  const [errors, setErrors] = useState<string | null>(null)

  const { isDesktop, isLarge } = useWindowSize()

  const outerClickRef = useOuterClick(() => {
    if (errors) setErrors(null)
  })

  return (
    <SDirectiveStepsNav>
      <SDirectiveStepsStack as="nav" space={20} ref={outerClickRef}>
        {(props.hideSteps ? [] : props.steps).map((step) => (
          <SDirectiveStepsStep
            key={step.value}
            isActive={step.isCurrent || step.isDone}
            disabled={props.disabled || step.disabled}
            data-testid={`${props.dataTestIdPrefix}-navigation-step-${step}`}
            onMouseOver={isDesktop ? () => setErrors(step.hasError) : undefined}
            onMouseLeave={isDesktop ? () => setErrors(null) : undefined}
            onClick={() => {
              if (!step.isCurrent) {
                router.push(path.resolve(router.asPath, `../${step.url}`))
              } else if (!isDesktop) {
                setErrors(step.hasError)
              }
            }}
          >
            <SDirectiveStepsTextContainer>
              {step.hasError && (
                <SDirectiveStepsValidationErrorIcon>
                  <Icon name="Warning" color={colors.red[500]} size="1rem" />
                </SDirectiveStepsValidationErrorIcon>
              )}
              <Text>{isLarge ? step.name : step.mobileName}</Text>
            </SDirectiveStepsTextContainer>

            <SDirectiveStepsProcessIcon>
              {step.isDone && !step.hasError ? (
                <Icon name="Check" color={colors.green[200]} size={20} />
              ) : step.isCurrent ? (
                <ProcessFocus />
              ) : (
                <ProcessUnchecked />
              )}
            </SDirectiveStepsProcessIcon>
          </SDirectiveStepsStep>
        ))}

        {errors && (
          <Portal>
            <BaseToast
              css={css`
                background: var(--c-red500);

                position: fixed;
                top: 2rem;
                left: 50%;
                transform: translate3d(-50%, 0, 10px);
                white-space: pre-wrap;
                z-index: 10;
              `}
            >
              {errors}
            </BaseToast>
          </Portal>
        )}
      </SDirectiveStepsStack>
      {props.progress}
    </SDirectiveStepsNav>
  )
}
